import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from 'constants/api';
import Api from 'util/Api';

// Una fija nace en su ruta. Una a demanda nace requested, sin ruta, y
// vuelve a requested si sale de la ruta. Si el driver se fue sin retirar o la
// ruta cerró sin pasar, queda not_collected y ahí termina.
export type CollectionStatus =
  | 'requested'
  | 'routed'
  | 'arrived'
  | 'collected'
  | 'ingesting'
  | 'received'
  | 'not_collected'
  | 'cancelled';

// Lo vivo y lo terminado, como Paquetes: En tránsito e Histórico.
export const TRANSIT_STATUSES: CollectionStatus[] = [
  'requested',
  'routed',
  'arrived',
  'collected',
  'ingesting',
];
export const HISTORY_STATUSES: CollectionStatus[] = [
  'received',
  'not_collected',
  'cancelled',
];

// Un turno del punto: a qué hora se colecta ahí y cuánto lleva. La colecta
// copia los dos valores al nacer, así que editar el turno no la toca.
export interface CollectionPointShift {
  id: number;
  startTime: string; // HH:MM
  estimatedDurationMinutes: number;
}

// Un punto por tienda; la hora y la duración son de cada turno.
export interface CollectionPoint {
  id: number;
  storeId: number;
  storeName: string;
  marketplaceId: number;
  marketplaceName: string;
  address: string;
  lat: number;
  lng: number;
  active: boolean;
  // El monto genérico: la colecta lo copia al nacer, como el turno.
  amount?: number | null;
  shifts: CollectionPointShift[];
  createdAt: string;
  updatedAt: string;
}

export type CollectionType = 'fixed' | 'on_demand';

export const COLLECTION_TYPE_LABELS: Record<CollectionType, string> = {
  fixed: 'Fija',
  on_demand: 'A demanda',
};

export const COLLECTION_TYPE_COLORS: Record<CollectionType, string> = {
  fixed: 'default',
  on_demand: 'magenta',
};

// Transitorio: sin tipo o con el viejo 'internal', es una fija.
export const collectionType = (type?: string | null): CollectionType =>
  type === 'on_demand' ? 'on_demand' : 'fixed';

export interface Collection {
  id: number;
  type: CollectionType;
  collectionPointId: number;
  tenantId: number;
  // Una Solicitada no tiene centro: lo toma al entrar a una ruta.
  depositId: number | null;
  depositName: string | null;
  depositAddress: string | null;
  depositLat: number | null;
  depositLng: number | null;
  marketplaceId: number;
  marketplaceName: string;
  storeId: number;
  storeName: string;
  address: string;
  lat: number;
  lng: number;
  scheduledStart: string;
  // Fin de la franja: solo las a demanda.
  scheduledEnd?: string | null;
  utcOffset?: string | null;
  estimatedDurationMinutes: number;
  status: CollectionStatus;
  amount?: number | null;
  // Bloqueado desde que su ruta se oferta: el monto ya no se edita.
  amountLocked?: boolean;
  // Lo que declaró el cliente al pedirla.
  declaredQuantity?: number | null;
  declaredDescription?: string | null;
  // Observaciones del cliente, solo en las a demanda.
  notes?: string | null;
  // La ruta en la que nació. Queda aunque termine: es su historia.
  journeyId: string | null;
  declaredPackageCount: number | null;
  // Lo que el operador declaró que recibió al ingresarla.
  receivedPackageCount: number | null;
  collectedAt: string | null;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
  // Los tres salen del historial y no de la fila. El listado los trae para que
  // el operador del depósito encuentre la colecta del camión que tiene enfrente
  // sin abrir el detalle.
  lastFailureReason?: string | null;
  driverName?: string | null;
  vehiclePlate?: string | null;
}

export type CollectionEvent =
  | 'requested'
  | 'routed'
  | 'released'
  | 'created'
  | 'transferred'
  | 'arrived'
  | 'collected'
  | 'left'
  | 'route_closed'
  | 'ingested'
  | 'received'
  | 'cancelled'
  | 'amount_updated'
  | 'updated';

// Lo que el evento guarda además del estado. Cada clave existe solo en los
// eventos que la escriben: packageCount y photoUrls en collected, waitedSeconds
// y photoUrls en left, y las coordenadas y measuredOnDevice en los tres del
// driver.
export interface CollectionLogMetadata {
  // Edición del cliente (updated): cada campo que cambió, con su valor antes y después.
  changes?: Record<string, { from: unknown; to: unknown }>;
  driverId?: number;
  driverName?: string;
  vehiclePlate?: string;
  lat?: number;
  lng?: number;
  measuredOnDevice?: boolean;
  packageCount?: number;
  waitedSeconds?: number;
  photoUrls?: string[];
  fromJourneyId?: string;
  // Ingreso al depósito: el código de cada escaneo y los números del cierre.
  externalCode?: string;
  scannedCount?: number;
  declaredPackageCount?: number;
  receivedPackageCount?: number;
  // La visita la cerró un operador liberando la espera desde el tablero.
  releasedByOperatorId?: number;
  releasedByOperatorName?: string;
  // La visita se registró antes de cumplir la espera, y cuánto faltaba.
  leftEarly?: boolean;
  remainingWaitSeconds?: number;
  // amount_updated: el monto antes y después.
  from?: number | null;
  to?: number | null;
}

export interface CollectionLog {
  id: number;
  event: CollectionEvent;
  fromStatus: CollectionStatus | null;
  toStatus: CollectionStatus;
  actorType: string;
  actorId: number | null;
  actorName: string | null;
  journeyId: string | null;
  reason: string | null;
  metadata: CollectionLogMetadata;
  occurredAt: string;
  createdAt: string;
}

export const COLLECTION_EVENT_LABELS: Record<CollectionEvent, string> = {
  requested: 'Pedida por el cliente',
  routed: 'Agregada a una ruta',
  released: 'Volvió a Solicitada',
  created: 'Creada en la ruta',
  transferred: 'Pasó a otra ruta',
  arrived: 'El driver llegó',
  collected: 'Retirada',
  left: 'Visita registrada sin retirar',
  route_closed: 'La ruta terminó sin pasar',
  ingested: 'Código escaneado en el depósito',
  received: 'Ingresada al depósito',
  cancelled: 'Cancelada',
  amount_updated: 'Cambió el monto',
  updated: 'Editada por el cliente',
};

// El backend guarda quién hizo cada evento en actorType y actorId, y el nombre
// solo cuando lo tiene: los avisos de ms-journey anteriores a MJ-09 no lo
// traían. Sin este respaldo un evento sin nombre no diría quién lo hizo.
// El CHECK de collection_logs admite además driver y store, del modelo viejo.
const ACTOR_TYPE_LABELS: Record<string, string> = {
  operator: 'Operador',
  journey: 'La ruta',
  system: 'El sistema',
  driver: 'Driver',
  store: 'Tienda',
  client: 'Cliente',
};

export const collectionActor = (log: {
  actorName: string | null;
  actorType: string;
  actorId: number | null;
}): string => {
  // El nombre del cliente es técnico (la integración y sus ids): no le dice nada a la operación.
  if (log.actorType === 'client') return 'El cliente, por API';
  if (log.actorName) return log.actorName;
  const label = ACTOR_TYPE_LABELS[log.actorType] ?? log.actorType;
  return log.actorId != null ? `${label} #${log.actorId}` : label;
};

export interface CollectionIntakeCode {
  externalCode: string;
  ingestedAt: string;
}

// El detalle sube driverName y vehiclePlate desde el historial: el remito y la
// pantalla de Ingreso los necesitan y ahi abajo quedarian enterrados en un log.
export interface CollectionDetail extends Collection {
  driverName: string | null;
  vehiclePlate: string | null;
  logs: CollectionLog[];
  intakeCodes: CollectionIntakeCode[];
  linkedCodesAmount: number;
}

interface Paged<T> {
  page: number;
  pageSize: number;
  totalElements: number;
  items: T[];
}

export interface ShiftPayload {
  startTime: string; // HH:MM
  estimatedDurationMinutes: number;
}

export interface CollectionPointPayload {
  storeId: number;
  storeName: string;
  marketplaceId: number;
  marketplaceName: string;
  address: string;
  lat: number;
  lng: number;
  amount?: number | null;
  // Solo en el alta. Después, los turnos van por sus propios endpoints.
  shifts?: ShiftPayload[];
}

// La colecta nace en su ruta, con la hora y la duración de esta colecta: salen
// del turno elegido o las cargó el operador, y no tocan el turno.
export interface CreateCollectionPayload {
  collectionPointId: number;
  journeyId: string;
  scheduledStart: string;
  estimatedDurationMinutes: number;
  // Si no viene, ms-collections copia el del punto; null es sin monto.
  amount?: number | null;
  // El centro de la ruta: ahí se ingresa la colecta. Sin él, ms-collections usa el del perfil.
  distributionCenterId?: number;
}

export interface CollectionPointFilters {
  q?: string;
  active?: boolean;
  storeId?: number;
  marketplaceId?: number;
  page?: number;
  pageSize?: number;
}

export interface CollectionFilters {
  status?: CollectionStatus[];
  type?: CollectionType[];
  q?: string;
  dateFrom?: string;
  dateTo?: string;
  storeId?: number;
  marketplaceId?: number;
  /** Las colectas por las que pasó ese driver, en cualquier intento (MC-07). */
  driverId?: number;
  journeyId?: string;
  collectionPointId?: number;
  page?: number;
  pageSize?: number;
}

const POINTS = 'points';
const COLLECTIONS = 'collections';
const PREFIX = 'collections-v2';

const pointsKey = (filters?: CollectionPointFilters) => [
  PREFIX,
  POINTS,
  filters ?? {},
];
const pointKey = (id: number) => [PREFIX, POINTS, id];
const collectionsKey = (filters?: CollectionFilters) => [
  PREFIX,
  COLLECTIONS,
  filters ?? {},
];
const collectionKey = (id: number) => [PREFIX, COLLECTIONS, id];
// Agregar o quitar una a demanda de una ruta la saca o la devuelve a la lista de Solicitadas.
export const COLLECTIONS_QUERY_KEY = [PREFIX, COLLECTIONS];
const codesKey = (id: number) => [PREFIX, COLLECTIONS, id, 'codes'];

// apiAxios resuelve 400, 409, 422 y 500 como respuestas normales, así que el
// error viaja en el cuerpo y hay que levantarlo a mano. El código del backend
// se conserva: la pantalla de Ingreso distingue duplicateCode de invalidStatus.
export class CollectionsApiError extends Error {
  code?: string;

  status: number;

  details?: string;

  constructor(status: number, body: any) {
    super(body?.error ?? 'No se pudo completar la operación.');
    this.name = 'CollectionsApiError';
    this.status = status;
    this.code = body?.code ?? body?.reason;
    this.details = body?.details;
  }
}

// El Result de antd tiene pantalla propia para 403, 404 y 500. Nuestros errores
// llegan como CollectionsApiError, que trae el status arriba; los que axios
// rechaza de verdad —red caída, timeout— lo traen adentro de response. Leer
// solo response.status, como hacen las listas de rutas, deja todos los nuestros
// en el cartel genérico.
export const collectionsResultStatus = (err: unknown) => {
  const status =
    (err as CollectionsApiError)?.status ??
    (err as { response?: { status?: number } })?.response?.status;
  if (status === 403 || status === 404 || status === 500) return status;
  return 'error' as const;
};

function unwrap<T>(response: { status: number; data: any }): T {
  if (response.status < 200 || response.status >= 300) {
    throw new CollectionsApiError(response.status, response.data);
  }
  return response.data as T;
}

const toSearchParams = (filters: object) => {
  const params = new URLSearchParams();
  Object.entries(filters as Record<string, unknown>).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value)) {
      value.forEach((item) => params.append(key, String(item)));
      return;
    }
    params.append(key, String(value));
  });
  return params.toString();
};

const withQuery = (url: string, filters: object) => {
  const query = toSearchParams(filters);
  return query ? `${url}?${query}` : url;
};

export const useCollectionPoints = (
  filters: CollectionPointFilters = {},
  enabled = true,
) =>
  useQuery({
    enabled,
    queryKey: pointsKey(filters),
    queryFn: async (): Promise<Paged<CollectionPoint>> => {
      const response = await Api.apiAxios.get(
        withQuery(api.collectionsV2.points, filters),
      );
      const data = unwrap<any>(response);
      return {
        items: data.points ?? [],
        page: data.page ?? 0,
        pageSize: data.pageSize ?? 0,
        totalElements: data.totalElements ?? 0,
      };
    },
    retry: 1,
  });

export const useCollectionPoint = (id?: number) =>
  useQuery({
    queryKey: pointKey(id as number),
    queryFn: async (): Promise<CollectionPoint> =>
      unwrap<CollectionPoint>(
        await Api.apiAxios.get(api.collectionsV2.point(id as number)),
      ),
    enabled: Boolean(id),
    retry: 1,
  });

export const useCreateCollectionPoint = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: CollectionPointPayload,
    ): Promise<CollectionPoint> =>
      unwrap<CollectionPoint>(
        await Api.apiAxios.post(api.collectionsV2.points, payload),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PREFIX, POINTS] });
    },
  });
};

export const useUpdateCollectionPoint = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...payload
    }: CollectionPointPayload & { id: number }): Promise<CollectionPoint> =>
      unwrap<CollectionPoint>(
        await Api.apiAxios.put(api.collectionsV2.point(id), payload),
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [PREFIX, POINTS] });
      queryClient.invalidateQueries({ queryKey: pointKey(variables.id) });
    },
  });
};

// Desactivar es DELETE y reactivar es PUT con active: true. No hay borrado: el
// punto desactivado sale del selector del ruteador y sus colectas no se tocan.
export const useSetCollectionPointActive = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      active,
    }: {
      id: number;
      active: boolean;
    }): Promise<void> => {
      const response = active
        ? await Api.apiAxios.put(api.collectionsV2.point(id), { active: true })
        : await Api.apiAxios.delete(api.collectionsV2.point(id));
      unwrap<unknown>(response);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [PREFIX, POINTS] });
      queryClient.invalidateQueries({ queryKey: pointKey(variables.id) });
    },
  });
};

// Los turnos se operan de a uno y cada llamada devuelve el punto entero con
// sus turnos.
const invalidatePoints = (
  queryClient: ReturnType<typeof useQueryClient>,
  id: number,
) => {
  queryClient.invalidateQueries({ queryKey: [PREFIX, POINTS] });
  queryClient.invalidateQueries({ queryKey: pointKey(id) });
};

export const useAddShift = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      pointId,
      ...payload
    }: ShiftPayload & { pointId: number }): Promise<CollectionPoint> =>
      unwrap<CollectionPoint>(
        await Api.apiAxios.post(api.collectionsV2.shifts(pointId), payload),
      ),
    onSuccess: (_data, variables) =>
      invalidatePoints(queryClient, variables.pointId),
  });
};

export const useUpdateShift = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      pointId,
      shiftId,
      ...payload
    }: ShiftPayload & {
      pointId: number;
      shiftId: number;
    }): Promise<CollectionPoint> =>
      unwrap<CollectionPoint>(
        await Api.apiAxios.put(
          api.collectionsV2.shift(pointId, shiftId),
          payload,
        ),
      ),
    onSuccess: (_data, variables) =>
      invalidatePoints(queryClient, variables.pointId),
  });
};

export const useDeleteShift = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      pointId,
      shiftId,
    }: {
      pointId: number;
      shiftId: number;
    }): Promise<CollectionPoint> =>
      unwrap<CollectionPoint>(
        await Api.apiAxios.delete(api.collectionsV2.shift(pointId, shiftId)),
      ),
    onSuccess: (_data, variables) =>
      invalidatePoints(queryClient, variables.pointId),
  });
};

export const useCollections = (
  filters: CollectionFilters = {},
  enabled = true,
) =>
  useQuery({
    enabled,
    queryKey: collectionsKey(filters),
    queryFn: async (): Promise<Paged<Collection>> => {
      const response = await Api.apiAxios.get(
        withQuery(api.collectionsV2.list, filters),
      );
      const data = unwrap<any>(response);
      return {
        items: data.collections ?? [],
        page: data.page ?? 0,
        pageSize: data.pageSize ?? 0,
        totalElements: data.totalElements ?? 0,
      };
    },
    retry: 1,
  });

export const useCollection = (id?: number) =>
  useQuery({
    queryKey: collectionKey(id as number),
    queryFn: async (): Promise<CollectionDetail> =>
      unwrap<CollectionDetail>(
        await Api.apiAxios.get(api.collectionsV2.detail(id as number)),
      ),
    enabled: Boolean(id),
    retry: 1,
  });

// La usa el ruteador en WT-04, no el módulo de colectas: la colecta nace cuando
// el operador agrega un punto a una ruta.
export const useCreateCollection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateCollectionPayload): Promise<Collection> =>
      unwrap<Collection>(
        await Api.apiAxios.post(api.collectionsV2.create, payload),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PREFIX, COLLECTIONS] });
    },
  });
};

// Cancela, o borra: si ms-journey dice que la ruta no la tiene, ms-collections
// la borra en vez de cancelarla (204). Es el mismo DELETE; la diferencia la
// decide el backend. El motivo es opcional; el cliente lo ve si era suya.
export const useCancelCollection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      reason,
    }: {
      id: number;
      reason?: string;
    }): Promise<void> => {
      unwrap<unknown>(
        await Api.apiAxios.delete(
          api.collectionsV2.cancel(id),
          reason ? { data: { reason } } : undefined,
        ),
      );
    },
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: [PREFIX, COLLECTIONS] });
      queryClient.invalidateQueries({ queryKey: collectionKey(id) });
    },
  });
};

// El monto se edita siempre en ms-collections, tenga ruta o no, mientras no
// esté bloqueado (si no, 409 con el motivo). ms-collections le avisa a
// ms-journey, que actualiza su copia y el total de la ruta después: por eso la
// ruta se refresca otra vez un rato más tarde.
const ROUTE_REFETCH_DELAY_MS = 2000;

export const useUpdateCollectionAmount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      amount,
    }: {
      id: number;
      amount: number | null;
      journeyId?: string | null;
    }): Promise<void> => {
      unwrap<unknown>(
        await Api.apiAxios.patch(api.collectionsV2.update(id), { amount }),
      );
    },
    onSuccess: (_data, { id, journeyId }) => {
      queryClient.invalidateQueries({ queryKey: [PREFIX, COLLECTIONS] });
      queryClient.invalidateQueries({ queryKey: collectionKey(id) });
      if (journeyId) {
        const refreshRoute = () =>
          queryClient.invalidateQueries({ queryKey: ['journey', journeyId] });
        refreshRoute();
        setTimeout(refreshRoute, ROUTE_REFETCH_DELAY_MS);
      }
    },
  });
};

// A diferencia del servicio viejo, un código repetido o un estado inválido
// levantan el error con su código para que la pantalla lo muestre.
export const useIngestCode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      externalCode,
    }: {
      id: number;
      externalCode: string;
    }): Promise<void> => {
      unwrap<unknown>(
        await Api.apiAxios.put(api.collectionsV2.ingest(id), { externalCode }),
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: codesKey(variables.id) });
      queryClient.invalidateQueries({ queryKey: collectionKey(variables.id) });
    },
  });
};

export const useFulfillCollection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      receivedPackageCount,
    }: {
      id: number;
      receivedPackageCount: number;
    }): Promise<void> => {
      unwrap<unknown>(
        await Api.apiAxios.put(api.collectionsV2.fulfill(id), {
          receivedPackageCount,
        }),
      );
    },
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: [PREFIX, COLLECTIONS] });
      queryClient.invalidateQueries({ queryKey: collectionKey(id) });
    },
  });
};

export const useCollectionCodes = (id?: number) =>
  useQuery({
    queryKey: codesKey(id as number),
    queryFn: async (): Promise<CollectionIntakeCode[]> => {
      const data = unwrap<any>(
        await Api.apiAxios.get(api.collectionsV2.codes(id as number)),
      );
      return data.codes ?? data ?? [];
    },
    enabled: Boolean(id),
    retry: 1,
  });

export const COLLECTION_STATUS_LABELS: Record<CollectionStatus, string> = {
  requested: 'Solicitada',
  routed: 'En ruta',
  arrived: 'Driver en el lugar',
  collected: 'Retirada (en el camión)',
  ingesting: 'Ingresando',
  received: 'Recibida',
  not_collected: 'No colectada',
  cancelled: 'Cancelada',
};

export const COLLECTION_STATUS_COLORS: Record<CollectionStatus, string> = {
  requested: 'purple',
  routed: 'blue',
  arrived: 'cyan',
  collected: 'orange',
  ingesting: 'gold',
  received: 'green',
  not_collected: 'volcano',
  cancelled: 'red',
};

export { collectionWindow } from 'util/collectionWindow';

export const declaredText = (
  quantity?: number | null,
  description?: string | null,
) =>
  [quantity, description]
    .filter((part) => part != null && part !== '')
    .join(' ');
